/**
 * TemplateEditor Component
 *
 * Main template editing interface for the V2 denormalized template system.
 * Sections have inline content (no module references).
 *
 * Displays:
 * - Editable template name
 * - Description
 * - Last saved timestamp
 * - Sections list with drag-and-drop
 */

import React from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { EDITOR_MAX_WIDTH_WITH_PADDING } from '../constants/layout';
import type { SectionWithMeta } from '../types';

import { TemplateSectionsList } from './TemplateSectionsList';

interface TemplateEditorProps {
  /** Template name */
  templateName: string;
  /** Template description */
  templateDescription: string | null;
  /** Template sections */
  sections: SectionWithMeta[];
  /** Whether template is loading */
  isLoading: boolean;
  /** Whether template can be edited */
  canEdit: boolean;
  /** Whether template can be forked */
  canFork: boolean;
  /** Template owner scope */
  ownerScope?: 'user' | 'org' | 'public';
  /** Whether template is locked (cannot edit or fork) */
  isLocked?: boolean;
  /** Whether title is being edited */
  isTitleEditing: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Callback to start editing title */
  onStartEditingTitle: () => void;
  /** Callback when title changes */
  onTitleChange: (title: string) => void;
  /** Callback when title editing finishes */
  onFinishEditingTitle: () => void | Promise<void>;
  /** Callback when description changes */
  onDescriptionChange: (description: string) => void;
  /** Callback to reorder sections */
  onReorderSections: (startIndex: number, endIndex: number) => void;
  /** Callback to remove section */
  onRemoveSection: (sectionId: string) => void;
  /** Callback to add section */
  onAddSection: (section: Omit<SectionWithMeta, 'showPreview'>, index?: number) => void;
  /** Callback to update section content */
  onUpdateSectionContent: (sectionId: string, content: string) => Promise<boolean>;
  /** Callback to update section title */
  onUpdateSectionTitle: (sectionId: string, title: string) => void;
  /** Callback to open section library modal with insertion index */
  onBrowseLibrary?: (index: number) => void;
  /** Linked appointment-types section, data-wired by the container (GDN-2149) */
  linkedTypesSection?: React.ReactNode;
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  templateName,
  templateDescription,
  sections,
  isLoading,
  canEdit,
  canFork,
  ownerScope,
  isLocked,
  isTitleEditing,
  lastSaved,
  onStartEditingTitle,
  onTitleChange,
  onFinishEditingTitle,
  onReorderSections,
  onRemoveSection,
  onAddSection,
  onUpdateSectionContent,
  onUpdateSectionTitle,
  onBrowseLibrary,
  linkedTypesSection,
}) => {
  const canEditOrCopy = canEdit || canFork;
  const isLockedOrgTemplate = ownerScope === 'org' && isLocked;

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onFinishEditingTitle();
    }
  };

  // Get permission-based help text
  const getHelpText = () => {
    if (canEdit) {
      return 'Drag sections to reorder. Click between sections to add new ones.';
    }
    if (canFork) {
      return 'This is a read-only template. Editing will create your own copy of this template.';
    }
    return 'This template is read-only.';
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: EDITOR_MAX_WIDTH_WITH_PADDING,
          mx: 'auto',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Skeleton variant="text" sx={{width: "60%", height: 48,  mb: 2 }} />
        <Skeleton variant="text" sx={{width: "40%", height: 24,  mb: 4 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              sx={{height: 72,  borderRadius: 2 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: EDITOR_MAX_WIDTH_WITH_PADDING,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Template Header. When the linked appointment-types block follows (GDN-2149),
          use the standard 16px rhythm so its gaps are symmetric; otherwise keep the
          larger metadata→sections break. */}
      <Box sx={{ mb: linkedTypesSection ? 2 : 4 }}>
        {/* Title */}
        <Box sx={{ mb: 2 }}>
          {isTitleEditing ? (
            <TextField
              value={templateName}
              onChange={e => onTitleChange(e.target.value)}
              onBlur={onFinishEditingTitle}
              onKeyDown={handleTitleKeyDown}
              autoFocus
              fullWidth
              variant="standard"
             slotProps={{ input: {
                sx: {
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  '& input': {
                    padding: 0,
                  },
                },
                disableUnderline: false,
              } }}/>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="h4"
                color="text.primary"
                sx={{fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  lineHeight: 1.3,
                }}
              >
                {templateName}
              </Typography>
              {canEditOrCopy && (
                <Tooltip title="Edit name">
                  <IconButton
                    size="small"
                    onClick={onStartEditingTitle}
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Description */}
        {templateDescription && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{fontWeight: 600,  mb: 0.5 }}
            >
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {templateDescription}
            </Typography>
          </Box>
        )}

        {/* Linked appointment types (GDN-2149) */}
        {linkedTypesSection}

        {/* Subtle read-only indicator for locked org templates */}
        {isLockedOrgTemplate && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: 'info.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light',
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              color="info.dark"
              sx={{fontWeight: 500,  fontSize: '0.8125rem' }}
            >
              This organization template is read-only. Only organization admins can edit it.
            </Typography>
          </Box>
        )}

        {/* Last Saved Indicator */}
        {lastSaved && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: 'success.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.light',
            }}
          >
            <Typography
              variant="body2"
              color="success.dark" sx={{ fontWeight: 500 }}
            >
              Last saved:{' '}
              {lastSaved.toLocaleString('en-US', { timeZoneName: 'short' })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Sections Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          color="text.primary" sx={{ fontWeight: 600 }}
        >
          Sections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getHelpText()}
        </Typography>
      </Box>

      {/* Sections List */}
      <TemplateSectionsList
        sections={sections}
        canEdit={canEditOrCopy}
        onReorderSections={onReorderSections}
        onRemoveSection={onRemoveSection}
        onAddSection={onAddSection}
        onUpdateSectionContent={onUpdateSectionContent}
        onUpdateSectionTitle={onUpdateSectionTitle}
        onBrowseLibrary={onBrowseLibrary}
      />
    </Box>
  );
};
